import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Card } from '../components/SubscriptionAppCard';
import { Input } from '../components/SubscriptionAppInput';
import { useSubscriptionForm } from '../hooks/useSubscriptionForm';
import { mockEvents, availableProducts, availableTags, mockPaymentRetryHistory } from '../data/mockSubscriptionData';
import { CreditCard, Calendar, ShoppingCart, X, Package, AlertTriangle, MoreHorizontal, PauseCircle, XCircle } from 'lucide-react';
import { OneTimeOrderModal } from '../components/subscription-detail/OneTimeOrderModal';
import type { OneTimeOrderLine } from '../components/subscription-detail/OneTimeOrderModal';
import { oneTimeOrderBundles } from '../data/mockOneTimeOrder';
import { subscriptionItemsToOrderLines } from '../utils/oneTimeOrder';
import { TagInput } from '../components/TagInput';
import type { TagData } from '../components/TagInput';
// Modular Components
import { SubscriptionHeader } from '../components/subscription-detail/SubscriptionHeader';
import { ProductSelector } from '../components/subscription-detail/ProductSelector';
import { AddressForm } from '../components/subscription-detail/AddressForm';
import { CustomerSummary } from '../components/subscription-detail/CustomerSummary';
import { ActivityTimeline, Event } from '../components/subscription-detail/ActivityTimeline';
import { OrdersTable } from '../components/OrdersTable';
import { PaymentMethodModal, PaymentData } from '../components/subscription-detail/PaymentMethodModal';
import { DateTimePicker } from '../components/subscription-detail/DateTimePicker';
import { PaymentStatusBanner } from '../components/subscription-detail/PaymentStatusBanner';
import { PaymentRetryHistory } from '../components/subscription-detail/PaymentRetryHistory';
import { SendPaymentLinkModal } from '../components/subscription-detail/SendPaymentLinkModal';
import { CreateTaskModal } from '../components/CreateTaskModal';
import type { TaskData } from '../components/CreateTaskModal';
import { useActivities } from '../contexts/ActivityContext';
import { useTags } from '../contexts/TagsContext';
import { Badge } from '../components/SubscriptionAppBadge';
import { BillingSchedulePreviewControl } from '../components/subscription-detail/BillingSchedulePreviewControl';
import { ScheduleStatus } from '../components/subscription-detail/ScheduleStatus';
import { FirstPackHeldBanner } from '../components/subscription-detail/FirstPackHeldBanner';
import { useBillingSchedulePreview, OFF_SCHEDULE_WARNING } from '../hooks/useBillingSchedulePreview';
import { parseScheduleDate } from '../utils/subscriptionSchedule';
// Shipping methods synced from Shopify (mocked)
const SHIPPING_METHODS = ['Flat Rate Shipping', 'Premium Shipping', 'Priority Shipping', 'Free Shipping'];
interface SubscriptionDetailPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SubscriptionDetailPage({
  currentPage = 'Subscriptions',
  onNavigate
}: SubscriptionDetailPageProps) {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  // Use onNavigate prop if provided, otherwise use local navigate
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else navigate('/');
  });
  const [copyShippingToBilling, setCopyShippingToBilling] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSendPaymentLinkModalOpen, setIsSendPaymentLinkModalOpen] = useState(false);
  const [isOrderNowModalOpen, setIsOrderNowModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [isOneTimeOrderOpen, setIsOneTimeOrderOpen] = useState(false);
  const [isLifecycleMenuOpen, setIsLifecycleMenuOpen] = useState(false);
  const lifecycleMenuRef = React.useRef<HTMLDivElement>(null);
  // Close the lifecycle "more" menu on outside click or Escape
  useEffect(() => {
    if (!isLifecycleMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (lifecycleMenuRef.current && !lifecycleMenuRef.current.contains(e.target as Node)) setIsLifecycleMenuOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLifecycleMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isLifecycleMenuOpen]);
  const [paymentMethod, setPaymentMethod] = useState({
    type: 'Visa',
    last4: '4242',
    expiry: '01/26'
  });
  // Customer contact state
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [initialCustomerEmail, setInitialCustomerEmail] = useState('');
  const [initialCustomerPhone, setInitialCustomerPhone] = useState('');
  // Helper function to check if card is expiring soon
  const isCardExpiringSoon = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
    return expiryDate <= sixtyDaysFromNow && expiryDate >= today;
  };
  const isCardExpired = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    return expiryDate < today;
  };
  const {
    loading,
    subscription,
    isEditing,
    setIsEditing,
    hasChanges: formHasChanges,
    formData,
    setFormData,
    selectedProducts,
    handleSave: originalHandleSave,
    handleCancel: originalHandleCancel,
    applyNextPayment,
    updateProductQuantity,
    currentTotalBottles,
    targetBottles,
    isBottleCountCorrect,
    subtotal,
    discountAmount,
    total
  } = useSubscriptionForm(id);
  // Pack billing schedule (rolling, fixed day or release dates), switchable for review
  const schedulePreview = useBillingSchedulePreview({
    nextPayment: subscription?.nextPayment,
    applyNextPayment
  });
  const editedNextPaymentDate = parseScheduleDate(formData.nextPayment);
  const isEditedDateOffSchedule = schedulePreview.isSynced && editedNextPaymentDate !== null && !schedulePreview.isDateOnSchedule(editedNextPaymentDate);
  // Get activities from shared context
  const {
    getActivitiesByCustomer,
    addActivity
  } = useActivities();
  // Link subscription to customer ID (in real app, this would come from subscription data)
  // For now, we'll use customer ID '1' for Sarah Johnson
  const customerId = '1'; // In real app: subscription?.customerId
  const subscriptionActivities = getActivitiesByCustomer(customerId);
  // Get tags from shared context
  const {
    allTags,
    getCustomerTags,
    addTagToCustomer,
    removeTagFromCustomer,
    createTag
  } = useTags();
  const subscriptionTags = getCustomerTags(customerId);
  // Calculate if customer contact info has changed
  const customerContactHasChanges = customerEmail !== initialCustomerEmail || customerPhone !== initialCustomerPhone;
  // Combined hasChanges that includes customer contact changes
  const hasChanges = formHasChanges || customerContactHasChanges;
  // Initialize customer contact info from subscription data
  useEffect(() => {
    if (subscription) {
      const email = subscription.customer;
      const phone = subscription.customerPhone || '+61 4XX XXX XXX';
      setCustomerEmail(email);
      setCustomerPhone(phone);
      setInitialCustomerEmail(email);
      setInitialCustomerPhone(phone);
    }
  }, [subscription]);
  // Tag handlers - use shared context
  const handleAddTag = (tag: TagData) => {
    addTagToCustomer(customerId, tag);
  };
  const handleRemoveTag = (tagId: string) => {
    removeTagFromCustomer(customerId, tagId);
  };
  // Create new tag handler
  const handleCreateTag = (label: string, color?: string) => {
    // Generate a unique ID for the new tag
    const newTagId = label.toLowerCase().replace(/\s+/g, '-');
    const newTag: TagData = {
      id: newTagId,
      label: label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    createTag(newTag);
    addTagToCustomer(customerId, newTag);
  };
  // Handle adding a note to the timeline
  const handleAddNote = (noteText: string) => {
    addActivity(customerId, {
      type: 'note',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: 'Admin Note',
      description: noteText,
      author: 'Sarah Admin'
    });
  };
  const handleAddTask = (task: TaskData) => {
    addActivity(customerId, {
      type: 'task',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: task.title,
      description: `Task assigned to ${task.assignee} - Priority: ${task.priority}. Due: ${task.dueDate}${task.dueTime ? ' ' + task.dueTime : ''}`,
      author: 'Sarah Admin'
    });
  };
  // Enhanced save handler that includes customer contact info
  const handleSave = () => {
    // Save customer contact changes
    console.log('Saving customer email:', customerEmail);
    console.log('Saving customer phone:', customerPhone);
    // Update initial values after save
    setInitialCustomerEmail(customerEmail);
    setInitialCustomerPhone(customerPhone);
    // Call original save handler
    originalHandleSave();
  };
  // Enhanced cancel handler that reverts customer contact info
  const handleCancel = () => {
    // Revert customer contact changes
    setCustomerEmail(initialCustomerEmail);
    setCustomerPhone(initialCustomerPhone);
    // Call original cancel handler
    originalHandleCancel();
  };
  // Format date for display (e.g., "15 Jan 2024, 9:00 AM")
  const formatDateTimeDisplay = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DD HH:MM" formats
    const [datePart, timePart] = dateTimeString.split(' ');
    if (!datePart) return dateTimeString;
    const [year, month, day] = datePart.split('-').map(Number);
    // Validate date parts
    if (!year || !month || !day) return dateTimeString;
    const date = new Date(year, month - 1, day);
    // Check if date is valid
    if (isNaN(date.getTime())) return dateTimeString;
    const dateStr = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    // If time part exists, format it
    if (timePart && timePart.includes(':')) {
      const [hours, minutes] = timePart.split(':').map(Number);
      // Validate time parts
      if (isNaN(hours) || isNaN(minutes)) return dateStr;
      const hour = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const minuteStr = minutes.toString().padStart(2, '0');
      return `${dateStr}, ${hour}:${minuteStr} ${ampm}`;
    }
    // If no time part, just return the date
    return dateStr;
  };
  // Handle date/time change from picker
  const handleDateTimeChange = (newDateTime: string) => {
    handleFormChange('nextPayment', newDateTime);
  };
  // Payment retry handlers
  const handleRetryNow = async () => {
    console.log('Retrying payment now...');
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Show success toast (in real app)
    alert('Payment retry initiated successfully!');
  };
  const handleSendPaymentLink = async (includeMessage: boolean, message: string) => {
    console.log('Sending payment link...', {
      includeMessage,
      message
    });
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Show success toast (in real app)
    alert(`Payment link sent to ${customerEmail}`);
  };
  const handleSendMagicLink = async () => {
    setIsSendingMagicLink(true);
    try {
      console.log('Sending magic link...');
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Show success toast (in real app)
      alert(`Magic link sent to ${customerEmail}`);
    } finally {
      setIsSendingMagicLink(false);
    }
  };
  // Get retry history for this subscription
  const retryHistory = mockPaymentRetryHistory[subscription?.id || ''] || [];
  // Helper to format payment status badge
  const getPaymentStatusBadge = () => {
    const paymentInfo = subscription?.paymentInfo;
    if (!paymentInfo || paymentInfo.status === 'healthy') {
      return <span className="text-[13px] text-[#94A3B8]" data-id="element-13425" data-mp="5eb2n00b01vh">—</span>;
    }
    if (paymentInfo.status === 'in-retry') {
      const isFinalRetry = paymentInfo.retryCount === paymentInfo.maxRetries;
      return <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${isFinalRetry ? 'bg-[#F59E0B] text-white border-[#F59E0B]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]'}`} data-id="element-13426" data-mp="2lwbnayrmo5qo">
          {isFinalRetry ? 'Final Retry' : `Retry ${paymentInfo.retryCount}/${paymentInfo.maxRetries}`}
        </span>;
    }
    if (paymentInfo.status === 'on-hold') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#FEF9C3] text-[#854D0E] border border-[#FDE047]" data-id="element-13427" data-mp="34n2z06z38888">
          On Hold
        </span>;
    }
    if (paymentInfo.status === 'failed') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#EF4444] text-white border border-[#EF4444]" data-id="element-13428" data-mp="1fa7u1n8xpej9">
          Failed
        </span>;
    }
    return null;
  };
  // Helper to format next retry date
  const formatNextRetry = (nextRetry?: string): string => {
    if (!nextRetry) return '—';
    const retryDate = new Date(nextRetry);
    const now = new Date();
    const diffMs = retryDate.getTime() - now.getTime();
    if (diffMs < 0) {
      return 'Overdue';
    }
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 24) {
      return `Tomorrow at ${retryDate.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    return retryDate.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    }) + ' at ' + retryDate.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const handlePaymentUpdate = (paymentData: PaymentData) => {
    const firstDigit = paymentData.cardNumber.charAt(0);
    let cardType = 'Card';
    if (firstDigit === '4') cardType = 'Visa';else if (firstDigit === '5') cardType = 'Mastercard';else if (firstDigit === '3') cardType = 'Amex';
    const last4 = paymentData.cardNumber.replace(/\s/g, '').slice(-4);
    const expiry = `${paymentData.expiryMonth}/${paymentData.expiryYear}`;
    setPaymentMethod({
      type: cardType,
      last4,
      expiry
    });
  };
  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };
  const handleCopyShippingToBilling = (checked: boolean) => {
    setCopyShippingToBilling(checked);
    if (checked) {
      setFormData({
        ...formData,
        billingFirstName: formData.shippingFirstName,
        billingLastName: formData.shippingLastName,
        billingAddress1: formData.shippingAddress1,
        billingAddress2: formData.shippingAddress2,
        billingCity: formData.shippingCity,
        billingState: formData.shippingState,
        billingPostCode: formData.shippingPostCode,
        billingCountry: formData.shippingCountry
      });
    }
  };
  // Lifecycle changes go through the existing edit flow (status + pause duration)
  const startStatusChange = (status: 'Paused' | 'Cancelled') => {
    setIsLifecycleMenuOpen(false);
    setIsEditing(true);
    setFormData({
      ...formData,
      status
    });
  };
  if (loading || !subscription) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center" data-id="element-13429" data-mp="2kfms9f0q49g5">
        <div className="text-[#475569]" data-id="element-13430" data-mp="2fhurrwm0jf0c">Loading subscription details...</div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-13431" data-mp="2beev509y1enn">
      <BillingSchedulePreviewControl previewId={schedulePreview.previewId} onSelect={schedulePreview.selectPreview} firstPackHeld={schedulePreview.firstPackHeld} onFirstPackHeldChange={schedulePreview.setFirstPackHeld} data-id="element-13432" data-mp="2bhub05bfyrq4" />

      <SubscriptionHeader subscription={subscription} isEditing={isEditing} onEdit={() => setIsEditing(true)} onSave={handleSave} onCancel={handleCancel} onBack={() => navigate('/subscriptions')} onCreateOneTimeOrder={() => setIsOneTimeOrderOpen(true)} isSaveDisabled={false} hasChanges={hasChanges} data-id="element-13433" data-mp="4pjvboqsms2l" />

      {/* Payment Status Banner */}
      {subscription.paymentInfo && subscription.paymentInfo.status !== 'healthy' && <PaymentStatusBanner paymentInfo={subscription.paymentInfo} customerEmail={customerEmail} onRetryNow={handleRetryNow} onSendPaymentLink={() => setIsSendPaymentLinkModalOpen(true)} onSendMagicLink={handleSendMagicLink} data-id="element-13434" data-mp="h0at76k4yyvq" />}

      {schedulePreview.heldBannerMessage && <FirstPackHeldBanner message={schedulePreview.heldBannerMessage} data-id="element-13435" data-mp="3pf307ld3nvx6" />}

      <div className="flex-1 overflow-auto" data-id="element-13436" data-mp="3ph5mbub4t4wm">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-13437" data-mp="3udwdbcoh5kwt">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-13438" data-mp="38w9exiemtnm">
            {/* Subscription Details Card */}
            <Card padding="none" data-id="element-13439" data-mp="19mvk65wp98yr">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13440" data-mp="19nnihoholksq">
                <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13441" data-mp="1ep4h1dcp0v65">
                  Subscription Details
                </h3>
              </div>

              <div className="p-6 bg-white" data-id="element-13442" data-mp="1usqzc9i3pgky">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-13443" data-mp="1upweigv6dtr0">
                  <div data-id="element-13444" data-mp="29hnc2jv3rhrw">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13445" data-mp="29iyk4vvkzbot">
                      Created on
                    </label>
                    <div className="text-[14px] text-[#0F172A] font-medium" data-id="element-13446" data-mp="2igs9tnyub730">
                      {subscription.startDate}
                    </div>
                  </div>

                  <div data-id="element-13447" data-mp="2sfhlgz057vat">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13448" data-mp="2sfzmsa1nwzpl">
                      Next payment due
                    </label>
                    {isEditing ? <div className="relative" data-id="element-13449" data-mp="y1glubwrr49o">
                        <button onClick={() => setIsDatePickerOpen(true)} className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] text-left hover:border-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all flex items-center justify-between" data-id="element-13450" data-mp="y28i7oy6x610">
                          <span className="font-medium" data-id="element-13451" data-mp="2v0pbs7b83uoe">
                            {formatDateTimeDisplay(formData.nextPayment)}
                          </span>
                          <Calendar className="w-4 h-4 text-[#64748B]" data-id="element-13452" data-mp="2zx5x6tr1fuxs" />
                        </button>
                        {isEditedDateOffSchedule && <div className="mt-2 flex items-start gap-2 p-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB]" role="alert" data-id="element-13453" data-mp="2qyaq7fg66gtf">
                            <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-13454" data-mp="2c2lc03h877z9" />
                            <p className="text-[12px] text-[#92400E]" data-id="element-13455" data-mp="2h0woyn2thylp">
                              {OFF_SCHEDULE_WARNING}
                            </p>
                          </div>}
                      </div> : <div data-id="element-13456" data-mp="f7qekm1z3cj8">
                        <div className="text-[14px] text-[#0F172A] font-medium" data-id="element-13457" data-mp="f6xda0ls9f8h">
                          {formatDateTimeDisplay(subscription.nextPayment)}
                        </div>
                        {schedulePreview.isSynced && <ScheduleStatus isOffSchedule={schedulePreview.isOffSchedule} expectedNextLabel={schedulePreview.expectedNextLabel} onPutBackOnSchedule={schedulePreview.putBackOnSchedule} data-id="element-13458" data-mp="19shwk5zj2g23" />}
                      </div>}
                  </div>

                  <div data-id="element-13459" data-mp="22ii3sbhi4584">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13460" data-mp="22hyicksn6ypc">
                      Status
                    </label>
                    {isEditing ? <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" value={formData.status} onChange={e => handleFormChange('status', e.target.value)} data-id="element-13461" data-mp="1shxxuvj1ecjk">
                        <option value="Active" data-id="element-13462" data-mp="3r1umkm0ts9cb">Active</option>
                        <option value="Paused" data-id="element-13463" data-mp="3mz7uck08smel">Paused</option>
                        <option value="Cancelled" data-id="element-13464" data-mp="3n12e3uzs28h3">Cancelled</option>
                        <option value="On Hold" data-id="element-13465" data-mp="4li51k0g4s2l">On Hold (tag applied)</option>
                      </select> : <div className="inline-flex" data-id="element-13466" data-mp="3vze4bvwqf490">
                        <span className={`px-3 py-1.5 rounded-md text-[12px] font-medium ${subscription.status === 'Active' ? 'bg-[#D1FAE5] text-[#059669] border border-[#BBF7D0]' : subscription.status === 'Paused' ? 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE]' : subscription.status === 'Cancelled' ? 'bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]' : 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]'}`} data-id="element-13467" data-mp="3vxt5hu3dwz5c">
                          {subscription.status}
                        </span>
                      </div>}
                  </div>

                  {isEditing && formData.status === 'Paused' && <div className="md:col-span-3" data-id="element-13468" data-mp="1lz2tqkkzkn98">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13469" data-mp="oa18lu8shu8q">
                        Pause Duration
                      </label>
                      <select className="w-full md:w-1/3 h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" value={formData.pauseDuration} onChange={e => handleFormChange('pauseDuration', e.target.value)} data-id="element-13470" data-mp="y7fb0pxvoii1">
                        <option value="" data-id="element-13471" data-mp="3fg8mlz8qi5iz">Select Duration</option>
                        <option value="1 Cycle" data-id="element-13472" data-mp="3fdcmq5dbt6rw">1 Cycle</option>
                        <option value="2 Cycles" data-id="element-13473" data-mp="3afuigsa97fq8">2 Cycles</option>
                        <option value="3 Cycles" data-id="element-13474" data-mp="36eitmoz7eyj3">3 Cycles</option>
                        <option value="Indefinite" data-id="element-13475" data-mp="36gvk5qj12h7r">Until Reactivated</option>
                      </select>
                    </div>}
                </div>
              </div>

              {/* Lifecycle actions - single row, never wraps */}
              {!isEditing && <div className="px-6 pb-6 bg-white" data-id="element-13476" data-mp="zpmdl8vu4tzb">
                  <div className="flex flex-nowrap items-center gap-3" data-id="element-13477" data-mp="3sj1odwc0q290">
                    <button type="button" onClick={() => setIsOrderNowModalOpen(true)} className="inline-flex items-center gap-2 h-10 px-5 whitespace-nowrap flex-shrink-0 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] active:bg-[#0369A1] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 ease-in-out" data-id="element-13478" data-mp="3sjul04h9jbdf">
                      <ShoppingCart className="w-[18px] h-[18px]" data-id="element-13479" data-mp="39riklna66k5x" />
                      Early Renewal
                    </button>
                    {subscription.status !== 'Paused' && subscription.status !== 'Cancelled' && <button type="button" onClick={() => startStatusChange('Paused')} className="inline-flex items-center gap-2 h-10 px-5 whitespace-nowrap flex-shrink-0 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] hover:border-[#94A3B8] transition-all duration-200" data-id="element-13480" data-mp="30t6qvce0b699">
                        <PauseCircle className="w-[18px] h-[18px]" data-id="element-13481" data-mp="2vuvb6l1161j4" />
                        Pause subscription
                      </button>}
                    {subscription.status !== 'Cancelled' && <div className="relative flex-shrink-0" ref={lifecycleMenuRef} data-id="element-13482" data-mp="279h0185h704x">
                        <button type="button" onClick={() => setIsLifecycleMenuOpen(open => !open)} aria-label="More subscription actions" aria-haspopup="menu" aria-expanded={isLifecycleMenuOpen} className="w-10 h-10 flex items-center justify-center rounded-md border border-[#CBD5E1] bg-white text-[#475569] hover:bg-[#F1F5F9] hover:border-[#94A3B8] transition-all duration-200" data-id="element-13483" data-mp="2m5y6pm2pzql5">
                          <MoreHorizontal className="w-[18px] h-[18px]" data-id="element-13484" data-mp="34umohz5wpfsp" />
                        </button>
                        {isLifecycleMenuOpen && <div role="menu" className="absolute left-0 top-full mt-1 w-52 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-20 py-1" data-id="element-13485" data-mp="2zxd2kthq7m9m">
                            <button type="button" role="menuitem" onClick={() => startStatusChange('Cancelled')} className="w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] font-medium text-[#EF4444] hover:bg-[#FEF2F2] transition-colors" data-id="element-13486" data-mp="39vamx5wgsywy">
                              <XCircle className="w-4 h-4" data-id="element-13487" data-mp="3luxwkxq4e9mm" />
                              Cancel subscription
                            </button>
                          </div>}
                      </div>}
                  </div>
                </div>}
            </Card>

            {/* Items / Wine Bottles Section */}
            <ProductSelector isEditing={isEditing} formData={formData} setFormData={setFormData} selectedProducts={selectedProducts} updateProductQuantity={updateProductQuantity} availableProducts={availableProducts} currentTotalBottles={currentTotalBottles} targetBottles={targetBottles} isBottleCountCorrect={isBottleCountCorrect} subscriptionItems={subscription.items} subtotal={subtotal} discountPercent={subscription.discountPercent} discountAmount={discountAmount} total={total} subscriptionTitle={schedulePreview.scheduleLabel ? `${subscription.subscriptionTitle.replace(/\s*Every .*$/, '')} · ${schedulePreview.scheduleLabel}` : subscription.subscriptionTitle} data-id="element-13488" data-mp="3lfaxrf6glst" />

            {/* Orders Table - NEW COMPONENT */}
            <OrdersTable onSkipOrder={schedulePreview.skipNextRelease} upcomingOrders={schedulePreview.decorateUpcomingOrders([{
            id: 'upcoming-1',
            date: subscription.nextPayment,
            orderId: '#4012',
            chargeId: '#1210458823',
            amount: '$126.50',
            status: 'pending',
            items: [{
              name: '2025 Fiano',
              quantity: 2,
              price: '$35.00'
            }, {
              name: '2024 Shiraz',
              quantity: 2,
              price: '$45.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: subscription.nextPayment,
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-2',
            date: '2024-02-15',
            orderId: '#4013',
            chargeId: '#1210458824',
            amount: '$142.00',
            status: 'pending',
            items: [{
              name: '2024 Cabernet Sauvignon',
              quantity: 3,
              price: '$48.00'
            }, {
              name: '2025 Rosé',
              quantity: 3,
              price: '$32.00'
            }],
            subtotal: '$240.00',
            taxes: '$24.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$264.00',
            chargeCreatedAt: '2024-02-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-3',
            date: '2024-03-15',
            orderId: '#4014',
            chargeId: '#1210458825',
            amount: '$98.50',
            status: 'pending',
            items: [{
              name: '2025 Sauvignon Blanc',
              quantity: 2,
              price: '$38.00'
            }, {
              name: '2024 Merlot',
              quantity: 2,
              price: '$42.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: '2024-03-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-4',
            date: '2024-04-15',
            orderId: '#4015',
            chargeId: '#1210458826',
            amount: '$156.00',
            status: 'pending',
            items: [{
              name: '2024 Pinot Noir',
              quantity: 4,
              price: '$52.00'
            }, {
              name: '2025 Chardonnay',
              quantity: 2,
              price: '$40.00'
            }],
            subtotal: '$288.00',
            taxes: '$28.80',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$316.80',
            chargeCreatedAt: '2024-04-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }])} recentOrders={schedulePreview.decorateRecentOrders([{
            id: '1',
            date: '2023-12-15',
            orderId: '#3873',
            chargeId: '#1180247974',
            amount: '$126.50',
            status: 'success',
            isCheckout: true,
            items: [{
              name: '2025 Fiano',
              quantity: 2,
              price: '$35.00'
            }],
            subtotal: '$70.00',
            taxes: '$7.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$77.00',
            chargeCreatedAt: '2023-12-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '2',
            date: '2023-11-15',
            orderId: '#3872',
            chargeId: '#1180247973',
            amount: '$142.00',
            status: 'success',
            items: [{
              name: '2024 Shiraz',
              quantity: 3,
              price: '$45.00'
            }, {
              name: '2024 Grenache',
              quantity: 3,
              price: '$38.00'
            }],
            subtotal: '$249.00',
            taxes: '$24.90',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$273.90',
            chargeCreatedAt: '2023-11-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '3',
            date: '2023-10-15',
            orderId: '#3871',
            chargeId: '#1180247972',
            amount: '$98.50',
            status: 'success',
            items: [{
              name: '2024 Riesling',
              quantity: 2,
              price: '$36.00'
            }, {
              name: '2024 Tempranillo',
              quantity: 2,
              price: '$44.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: '2023-10-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '4',
            date: '2023-09-15',
            orderId: '#3870',
            chargeId: '#1180247971',
            amount: '$134.00',
            status: 'success',
            items: [{
              name: '2024 Cabernet Sauvignon',
              quantity: 2,
              price: '$48.00'
            }, {
              name: '2024 Pinot Grigio',
              quantity: 2,
              price: '$35.00'
            }, {
              name: '2024 Syrah',
              quantity: 2,
              price: '$46.00'
            }],
            subtotal: '$258.00',
            taxes: '$25.80',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$283.80',
            chargeCreatedAt: '2023-09-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }])} data-id="element-13489" data-mp="sjqqb06xafv5" />

            {/* Payment Retry History - Only show if there's retry history */}
            {retryHistory.length > 0 && <PaymentRetryHistory retryAttempts={retryHistory} data-id="element-13490" data-mp="ve4kx73zfdii" />}

            {/* Activity Timeline */}
            <ActivityTimeline events={subscriptionActivities} onAddNote={handleAddNote} onAddTask={() => setIsCreateTaskModalOpen(true)} data-id="element-13491" data-mp="qfjkp7oqctu2" />
          </div>

          {/* Right Column - Customer Summary & Addresses */}
          <div className="space-y-6" data-id="element-13492" data-mp="2frv3fyi7acz7">
            <CustomerSummary customerName={subscription.customerName} customerEmail={customerEmail} customerPhone={customerPhone} totalPaid="$1,240.00" totalSaved="$124.00" isEditing={isEditing} onEmailChange={setCustomerEmail} onPhoneChange={setCustomerPhone} onViewInShopify={() => window.open('https://admin.shopify.com/customers', '_blank', 'noopener,noreferrer')} onViewContact={() => navigate('/contacts/1')} onSendMagicLink={handleSendMagicLink} isSendingMagicLink={isSendingMagicLink} onOpenAsCustomer={() => window.open('https://account.example-winery.com.au/', '_blank', 'noopener,noreferrer')} data-id="element-13493" data-mp="2fqtrlrtd1mgf" />

            {/* View Contact Button */}
            <div className="px-6 -mt-3" data-id="element-13494" data-mp="36zn7upye8jav"></div>

            {/* Payment Status Card - Only show if not healthy */}
            {subscription.paymentInfo && subscription.paymentInfo.status !== 'healthy' && <Card padding="none" data-id="element-13495" data-mp="2s8wevel2fe66">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13496" data-mp="2x91ziasx40er">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13497" data-mp="3kyd463de2ey6">
                      Payment Status
                    </h3>
                  </div>
                  <div className="p-6 bg-white space-y-4" data-id="element-13498" data-mp="1pnvei8j0nlwx">
                    <div data-id="element-13499" data-mp="1ppg91y5bz4us">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13500" data-mp="1ppq0lq60xk2l">
                        Status
                      </label>
                      <div data-id="element-13501" data-mp="1gwcwds2iew17">{getPaymentStatusBadge()}</div>
                    </div>

                    {subscription.paymentInfo.nextRetry && <div data-id="element-13502" data-mp="1bybdb7532wpq">
                        <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13503" data-mp="28mr2310dd21s">
                          Next Retry
                        </label>
                        <div className="text-[14px] text-[#0F172A]" data-id="element-13504" data-mp="24lxfqc2krvb0">
                          {formatNextRetry(subscription.paymentInfo.nextRetry)}
                        </div>
                      </div>}
                  </div>
                </Card>}

            {/* Payment Method Card */}
            <Card padding="none" data-id="element-13505" data-mp="o8h3kyg29o9p">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13506" data-mp="o4jja9hqyqo8">
                <div className="flex items-center justify-between" data-id="element-13507" data-mp="93w6udxrhaf5">
                  <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13508" data-mp="95penwk420oq">
                    Payment Method
                  </h3>
                  <button onClick={() => setIsPaymentModalOpen(true)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-13509" data-mp="j223ag0stocm">
                    Update
                  </button>
                </div>
              </div>

              <div className="p-6 bg-white" data-id="element-13510" data-mp="rtubek2cole1">
                <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-13511" data-mp="wt6xh2ledofk">
                  <CreditCard className="w-5 h-5 text-[#475569]" data-id="element-13512" data-mp="11s0ev63t1zl0" />
                  <div className="flex-1" data-id="element-13513" data-mp="11qpap56g6ptd">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-13514" data-mp="16rwj96gezt13">
                      {paymentMethod.type} ending in {paymentMethod.last4}
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-id="element-13515" data-mp="38ipzzx252gao">
                      <span className="text-[12px] text-[#94A3B8]" data-id="element-13516" data-mp="38l3wmacgw0kc">
                        Expires {paymentMethod.expiry}
                      </span>
                      {isCardExpired(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]" data-id="element-13517" data-mp="2dbjingbcntfc">
                          Expired
                        </span>}
                      {!isCardExpired(paymentMethod.expiry) && isCardExpiringSoon(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]" data-id="element-13518" data-mp="1plyumj0ca5z7">
                            Expiring Soon
                          </span>}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tags Card */}
            <Card padding="none" data-id="element-13519" data-mp="15z9ibygji90y">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13520" data-mp="1ayvx4cc0nxbv">
                <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13521" data-mp="13fd0325xu3ka">
                  Tags
                </h3>
              </div>
              <div className="p-6 bg-white" data-id="element-13522" data-mp="yhbg8f0ou07r">
                <TagInput tags={subscriptionTags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateTag} placeholder="Add or create tag..." data-id="element-13523" data-mp="yegw6oll6hev" />
                <p className="text-[12px] text-[#94A3B8] mt-2" data-id="element-13524" data-mp="phy3ndh9ovit">
                  Type a tag name and press Enter to create a new tag
                </p>
              </div>
            </Card>

            {/* Addresses - Compact view or editable forms */}
            {isEditing ? <>
                {/* Shipping Address Form */}
                <AddressForm type="shipping" formData={formData} onChange={(field, value) => handleFormChange(field, value)} disabled={false} data-id="element-13525" data-mp="5s4g5bll52ke" />

                {/* Delivery Instructions */}
                <Card padding="none" className="-mt-3" data-id="element-13526" data-mp="3swkepzwxhjwd">
                  <div className="p-6 bg-white" data-id="element-13527" data-mp="3sx3tx8v71u6p">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13528" data-mp="3j86ni4dbp963">
                      Delivery Instructions
                    </label>
                    <textarea value={formData.deliveryInstructions} onChange={e => handleFormChange('deliveryInstructions', e.target.value)} placeholder="e.g., Leave at front door, Call on arrival, etc." rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-[border-color,box-shadow] duration-200 ease-in-out resize-none" data-id="element-13529" data-mp="3n7yo9uwh6b0w" />
                  </div>
                </Card>

                {/* Shipping Method */}
                <Card padding="none" className="-mt-3" data-id="element-13530" data-mp="110ecvpdd3hy">
                  <div className="p-6 bg-white" data-id="element-13531" data-mp="ormmso3acpsh">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13532" data-mp="oquqfb1v6o15">
                      Shipping Method
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-13533" data-mp="2e8i6kh7y31j5">
                      {SHIPPING_METHODS.map(method => {
                    const selected = formData.shippingMethod === method;
                    return <label key={method} className={`flex items-center gap-2.5 h-10 px-3 bg-white border rounded-md text-[14px] cursor-pointer transition-all duration-200 ${selected ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9]/20' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-13534" data-mp="2n1lj8narnacq">
                            <input type="radio" name="shippingMethod" value={method} checked={selected} onChange={e => handleFormChange('shippingMethod', e.target.value)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-2 focus:ring-[#0EA5E9]/20 cursor-pointer" data-id="element-13535" data-mp="1qd5qyjg9umvx" />
                            <span className={selected ? 'text-[#0F172A] font-medium' : 'text-[#475569]'} data-id="element-13536" data-mp="3fq94wjzfg1qt">
                              {method}
                            </span>
                          </label>;
                  })}
                    </div>
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-13537" data-mp="9ykuciolukiy">
                      Synced from Shopify
                    </p>
                  </div>
                </Card>

                {/* Copy to Billing Checkbox */}
                <div className="flex items-center gap-2 px-6 -mt-3" data-id="element-13538" data-mp="oth4j1fz5hyo">
                  <input type="checkbox" id="copyToBilling" checked={copyShippingToBilling} onChange={e => handleCopyShippingToBilling(e.target.checked)} className="w-[18px] h-[18px] text-[#0EA5E9] border-2 border-[#CBD5E1] rounded focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" data-id="element-13539" data-mp="oro1rx8hfhvy" />
                  <label htmlFor="copyToBilling" className="text-[14px] text-[#0F172A] cursor-pointer select-none" data-id="element-13540" data-mp="12n8j4o8kbs19">
                    Billing address same as shipping
                  </label>
                </div>

                {/* Billing Address Form */}
                <AddressForm type="billing" formData={formData} onChange={(field, value) => handleFormChange(field, value)} disabled={false} data-id="element-13541" data-mp="1glye9ybtnhmz" />
              </> : <>
                {/* Compact Shipping Address View */}
                <Card padding="none" data-id="element-13542" data-mp="2wzsv1zmwwie7">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13543" data-mp="2s29h70u4m7ms">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13544" data-mp="2n3g2j533sal1">
                      Shipping Address
                    </h3>
                  </div>
                  <div className="p-6 bg-white" data-id="element-13545" data-mp="3kt18bjwc4639">
                    <div className="text-[14px] text-[#475569] leading-relaxed" data-id="element-13546" data-mp="3frlm3oif7tja">
                      <div className="font-medium text-[#0F172A]" data-id="element-13547" data-mp="3b0vjgj1dq4sa">
                        {formData.shippingFirstName} {formData.shippingLastName}
                      </div>
                      <div data-id="element-13548" data-mp="3607sfxd5hny2">{formData.shippingAddress1}</div>
                      {formData.shippingAddress2 && <div data-id="element-13549" data-mp="7e4wicbts2rf">{formData.shippingAddress2}</div>}
                      <div data-id="element-13550" data-mp="2kaf5fjozzvi">
                        {formData.shippingCity}, {formData.shippingState}{' '}
                        {formData.shippingPostCode}
                      </div>
                      <div data-id="element-13551" data-mp="20v0tjmrukjnk">{formData.shippingCountry}</div>
                    </div>

                    {/* Delivery Instructions */}
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-id="element-13552" data-mp="1n2unitiert1u">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13553" data-mp="1s1fh4a35nehb">
                        Delivery Instructions
                      </label>
                      {formData.deliveryInstructions ? <div className="text-[14px] text-[#475569] bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-13554" data-mp="1i5ci3d3lnvyl">
                          {formData.deliveryInstructions}
                        </div> : <div className="text-[14px] text-[#94A3B8] italic" data-id="element-13555" data-mp="192tg0v7n1nde">
                          No delivery instructions provided
                        </div>}
                    </div>

                    {/* Shipping Method */}
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-id="element-13556" data-mp="33gun23dcdgok">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-13557" data-mp="3dmm2slh5ble7">
                        Shipping Method
                      </label>
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-13558" data-mp="3mdmghi0u6qqx">
                        {formData.shippingMethod}
                      </div>
                      <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-13559" data-mp="3hluyhvvqxtce">
                        Synced from Shopify
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Compact Billing Address View */}
                <Card padding="none" data-id="element-13560" data-mp="97mdp9zgu6us">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13561" data-mp="96jkuwil1uc8">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13562" data-mp="53mxytm0fc2h">
                      Billing Address
                    </h3>
                  </div>
                  <div className="p-6 bg-white" data-id="element-13563" data-mp="26yfjwr17p32l">
                    <div className="text-[14px] text-[#475569] leading-relaxed" data-id="element-13564" data-mp="2vi2c62x6ioy7">
                      <div className="font-medium text-[#0F172A]" data-id="element-13565" data-mp="2vfy8nm36zryg">
                        {formData.billingFirstName} {formData.billingLastName}
                      </div>
                      <div data-id="element-13566" data-mp="3595f7a9o5e04">{formData.billingAddress1}</div>
                      {formData.billingAddress2 && <div data-id="element-13567" data-mp="398e8ke0sr9v5">{formData.billingAddress2}</div>}
                      <div data-id="element-13568" data-mp="2cj6lv2ofyupn">
                        {formData.billingCity}, {formData.billingState}{' '}
                        {formData.billingPostCode}
                      </div>
                      <div data-id="element-13569" data-mp="2hk49vvw4uiz2">{formData.billingCountry}</div>
                    </div>
                  </div>
                </Card>
              </>}
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handlePaymentUpdate} currentCard={paymentMethod} data-id="element-13570" data-mp="tw38k28q8f8" />

      {/* DateTime Picker Modal */}
      {isDatePickerOpen && <DateTimePicker value={formData.nextPayment} onChange={handleDateTimeChange} onClose={() => setIsDatePickerOpen(false)} highlightedDates={schedulePreview.isSynced ? schedulePreview.highlightedDates : undefined} highlightLegend={schedulePreview.isSynced ? "Pack's scheduled dates" : undefined} getWarning={schedulePreview.isSynced ? date => schedulePreview.isDateOnSchedule(date) ? null : OFF_SCHEDULE_WARNING : undefined} data-id="element-13571" data-mp="fwx80gpiums6" />}

      {/* Send Payment Link Modal */}
      <SendPaymentLinkModal isOpen={isSendPaymentLinkModalOpen} onClose={() => setIsSendPaymentLinkModalOpen(false)} onSend={handleSendPaymentLink} customerEmail={customerEmail} data-id="element-13572" data-mp="3ocgxr5qzhn2h" />

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={handleAddTask} initialLinkedTo={{
      id: subscription.id,
      type: 'subscription',
      label: subscription.subscriptionTitle
    }} data-id="element-13573" data-mp="3fc2ba32aa1xe" />

      {/* One-time Order Modal */}
      <OneTimeOrderModal isOpen={isOneTimeOrderOpen} onClose={() => setIsOneTimeOrderOpen(false)} customerName={subscription.customerName} initialLines={subscriptionItemsToOrderLines(subscription.items || [], availableProducts)} availableProducts={[...availableProducts, ...oneTimeOrderBundles]} shippingAddressLines={[`${formData.shippingFirstName} ${formData.shippingLastName}`, formData.shippingAddress1, formData.shippingAddress2, `${formData.shippingCity}, ${formData.shippingState} ${formData.shippingPostCode}`].filter(Boolean)} deliveryMethod={formData.shippingMethod} paymentMethodLabel={`${paymentMethod.type} ending ${paymentMethod.last4}`} data-id="element-13574" data-mp="22wtyjv7ag75d" />

      {/* Order Now Confirmation Modal */}
      {isOrderNowModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsOrderNowModalOpen(false)} data-id="element-13575" data-mp="1ow11b2plqxyk" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-13576" data-mp="35a99fs9ml4qo">
            <div className="bg-white rounded-lg shadow-xl w-[520px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-13577" data-mp="30d0qvqkkeyow">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-13578" data-mp="r42qy61h44o3">
                <div className="flex items-center gap-3" data-id="element-13579" data-mp="d5cvsvy7sf2d">
                  <div className="w-10 h-10 rounded-md bg-[#E0F2FE] flex items-center justify-center" data-id="element-13580" data-mp="i2d1h5r5jfsy">
                    <ShoppingCart className="w-5 h-5 text-[#0EA5E9]" data-id="element-13581" data-mp="15s7bzgo00sa1" />
                  </div>
                  <div data-id="element-13582" data-mp="19ukezxbd2c34">
                    <h2 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-13583" data-mp="19sr8qj4nttvn">
                      Create Order Now
                    </h2>
                    <p className="text-[13px] text-[#64748B]" data-id="element-13584" data-mp="w0tgrl32yemp">
                      Confirm order for {subscription.customerName}
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsOrderNowModalOpen(false)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-13585" data-mp="1opgv0wpew3qr">
                  <X className="w-5 h-5 text-[#64748B]" data-id="element-13586" data-mp="23ea8k9ba0wb5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-5 overflow-y-auto space-y-5" data-id="element-13587" data-mp="1yfp6s5gdm4kl">
                {/* Info Banner */}
                <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-md px-4 py-3 flex items-start gap-2.5" data-id="element-13588" data-mp="1tdzvqpt5lqwi">
                  <AlertTriangle className="w-4 h-4 text-[#0369A1] mt-0.5 shrink-0" data-id="element-13589" data-mp="1oj3z1ccz2fd2" />
                  <p className="text-[13px] text-[#0369A1]" data-id="element-13590" data-mp="1kh90gfmfeg2z">
                    This will immediately create and process an order using the
                    current selection below. The next payment date will reset
                    based on the billing cycle (
                    {formData.frequency.replace('Every ', '').toLowerCase()}{' '}
                    from today).
                  </p>
                </div>

                {/* Current Selection Table */}
                <div data-id="element-13591" data-mp="wqv9ksfkun9z">
                  <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-[0.5px] mb-3" data-id="element-13592" data-mp="wrogbtejdcs4">
                    Current Selection
                  </h3>
                  <div className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-13593" data-mp="3cd7havs4wrr0">
                    <table className="w-full" data-id="element-13594" data-mp="3hen4atdvh8c1">
                      <thead className="bg-[#F1F5F9]" data-id="element-13595" data-mp="3hbsjh0qy5li3">
                        <tr data-id="element-13596" data-mp="3mbnar5emwd3o">
                          <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-13597" data-mp="3mbwysng4ca6q">
                            Item
                          </th>
                          <th className="px-4 py-2.5 text-center text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-13598" data-mp="2toam8powxv7q">
                            Qty
                          </th>
                          <th className="px-4 py-2.5 text-right text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-13599" data-mp="33f5c041dbcxx">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E2E8F0]" data-id="element-13600" data-mp="ggjgig2hbihq">
                        {subscription.items?.map((item: any, index: number) => {
                      const product = availableProducts.find(p => p.name === item.name);
                      const unitPrice = product?.price || 0;
                      return <tr key={index} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-13601" data-mp="lg5w2w5s5atp">
                              <td className="px-4 py-3" data-id="element-13602" data-mp="104iq3nlb4jlk">
                                <div className="flex items-center gap-2" data-id="element-13603" data-mp="1074y1oc4114b">
                                  <Package className="w-4 h-4 text-[#94A3B8]" data-id="element-13604" data-mp="v5pg4558baq5" />
                                  <span className="text-[14px] text-[#0F172A]" data-id="element-13605" data-mp="194p192tj97hk">
                                    {item.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center" data-id="element-13606" data-mp="3az6gz83h19d9">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#F1F5F9] text-[13px] font-medium text-[#0F172A]" data-id="element-13607" data-mp="35zthnnvey9ts">
                                  {item.quantity}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-[14px] text-[#0F172A] font-medium" data-id="element-13608" data-mp="1cmusg2wiqy28">
                                ${(unitPrice * item.quantity).toFixed(2)}
                              </td>
                            </tr>;
                    })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-4 space-y-2" data-id="element-13609" data-mp="tqd47vgvpgaq">
                  <div className="flex items-center justify-between" data-id="element-13610" data-mp="xrexzws1105b">
                    <span className="text-[14px] text-[#475569]" data-id="element-13611" data-mp="2o2jg6whlk143">Subtotal</span>
                    <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-13612" data-mp="2s4eio4b9kkjg">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  {discountAmount > 0 && <div className="flex items-center justify-between" data-id="element-13613" data-mp="3w2i8nunu2ncv">
                      <span className="text-[14px] text-[#10B981]" data-id="element-13614" data-mp="3w4mlx37mgypt">
                        Discount ({subscription.discountPercent}%)
                      </span>
                      <span className="text-[14px] text-[#10B981] font-medium" data-id="element-13615" data-mp="3n2vg7yd30rvy">
                        -${discountAmount.toFixed(2)}
                      </span>
                    </div>}
                  <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]" data-id="element-13616" data-mp="jkv6adnyucfp">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-13617" data-mp="etlgnd2f393t">
                      Total
                    </span>
                    <span className="text-[16px] font-bold text-[#0EA5E9]" data-id="element-13618" data-mp="9v1txag01tae">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  {schedulePreview.orderNowSummary && <p className="flex items-start gap-2 pt-2 border-t border-[#E2E8F0] text-[13px] text-[#475569]" data-id="element-13619" data-mp="12io5b4vgy27t">
                      <Calendar className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-13620" data-mp="19xgpbt1409es" />
                      {schedulePreview.orderNowSummary}
                    </p>}
                </div>

                {/* Shipping Address */}
                <div data-id="element-13621" data-mp="1nves0162iheg">
                  <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-[0.5px] mb-2" data-id="element-13622" data-mp="1nvohzor44ok5">
                    Shipping To
                  </h3>
                  <div className="text-[14px] text-[#475569] leading-relaxed bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-3" data-id="element-13623" data-mp="1xst6vwccey4n">
                    <div className="font-medium text-[#0F172A]" data-id="element-13624" data-mp="22l458urfqbv3">
                      {formData.shippingFirstName} {formData.shippingLastName}
                    </div>
                    <div data-id="element-13625" data-mp="2bkzm9g7o9131">{formData.shippingAddress1}</div>
                    {formData.shippingAddress2 && <div data-id="element-13626" data-mp="2gjavppu219kq">{formData.shippingAddress2}</div>}
                    <div data-id="element-13627" data-mp="2gju2cae98bjg">
                      {formData.shippingCity}, {formData.shippingState}{' '}
                      {formData.shippingPostCode}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3 bg-white" data-id="element-13628" data-mp="3brnfsedzii86">
                <button onClick={() => setIsOrderNowModalOpen(false)} className="h-10 px-5 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] hover:border-[#94A3B8] transition-all duration-200" data-id="element-13629" data-mp="36u562ls57yz4">
                  Cancel
                </button>
                <button onClick={() => {
              console.log('Order created!', {
                items: subscription.items,
                total,
                shippingAddress: {
                  firstName: formData.shippingFirstName,
                  lastName: formData.shippingLastName,
                  address1: formData.shippingAddress1,
                  city: formData.shippingCity,
                  state: formData.shippingState,
                  postCode: formData.shippingPostCode
                }
              });
              setIsOrderNowModalOpen(false);
              alert('Order created successfully!');
            }} className="inline-flex items-center gap-2 h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] active:bg-[#0369A1] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-13630" data-mp="2svdw9bkbczgs">
                  <ShoppingCart className="w-4 h-4" data-id="element-13631" data-mp="yknymvmqd2yy" />
                  Confirm Order
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}